import React, { useState, useRef, useEffect } from 'react';
import { Container, Badge, Dropdown, Image, Offcanvas } from 'react-bootstrap';
import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom';
import NotificationBell from './NotificationBell';
import { useNotifications } from '../contexts/NotificationContext';
import { useAuth } from '../contexts/AuthContext';

const APP_VERSION = __APP_VERSION__;

const Dashboard = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { unreadCount } = useNotifications();
  const { user, logout } = useAuth();
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const userMenuRef = useRef(null);
  const mobileMenuRef = useRef(null);
  const currentYear = new Date().getFullYear();

  // Закрытие меню при клике вне его области
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
        setShowUserMenu(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const getActiveTab = () => {
    const path = location.pathname;
    if (path === '/dashboard/profile' || path === '/dashboard') return 'profile';
    if (path === '/dashboard/users') return 'users';
    if (path === '/dashboard/notifications') return 'notifications';
    if (path === '/dashboard/organizations') return 'organizations';
    if (path === '/dashboard/surveys') return 'surveys'; // Добавлено
    return 'profile';
  };

  const getRoleBadgeVariant = (role) => {
    switch (role) {
      case 'admin': return 'danger';
      case 'moderator': return 'warning';
      default: return 'secondary';
    }
  };

  const getRoleIcon = (role) => {
    switch (role) {
      case 'admin': return 'bi-star-fill';
      case 'moderator': return 'bi-shield-check';
      default: return 'bi-person';
    }
  };

  const getInitials = () => {
    const firstName = user?.telegram_first_name || '';
    const lastName = user?.telegram_last_name || '';
    
    if (firstName || lastName) {
      return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
    }
    
    if (user?.name) {
      return user.name
        .split(' ')
        .map(word => word.charAt(0))
        .join('')
        .toUpperCase()
        .substring(0, 2);
    }
    
    return 'U';
  };

  const getFullName = () => {
    const firstName = user?.telegram_first_name || '';
    const lastName = user?.telegram_last_name || '';
    
    if (firstName && lastName) {
      return `${firstName} ${lastName}`;
    } else if (firstName) {
      return firstName;
    } else if (lastName) {
      return lastName;
    }
    
    return user?.name || 'Пользователь';
  };

  const toggleUserMenu = () => {
    setShowUserMenu(!showUserMenu);
  };

  const toggleMobileMenu = () => {
    setShowMobileMenu(!showMobileMenu);
  };

  const handleLogout = () => {
    setShowUserMenu(false);
    setShowMobileMenu(false);
    logout();
  };

  const handleNavigation = (path) => {
    navigate(`/dashboard${path}`);
    setShowMobileMenu(false);
  };

  if (!user) {
    return null;
  }

  return (
    <div className="d-flex flex-column min-vh-100 bg-light">
      {/* Header */}
      <header className="dashboard-header bg-white border-bottom">
        <Container fluid className="px-3 px-lg-4">
          <div className="d-flex justify-content-between align-items-center py-2">
            
            {/* ЛЕВАЯ ЧАСТЬ */}
            <div className="d-flex align-items-center">
              {/* Кнопка мобильного меню */}
              <button
                className="mobile-menu-btn btn btn-link text-dark p-2 me-2 d-lg-none"
                onClick={toggleMobileMenu}
                aria-label="Открыть меню"
              >
                <i className="bi bi-list fs-5"></i>
              </button>

              {/* Логотип */}
            <div className="d-flex align-items-center">
              <div className="text-primary fs-3">
                <i className="bi bi-clipboard-check"></i>
              </div>
                <div className="d-none d-md-block ms-3">
                  <h1 className="h6 mb-0 text-primary fw-bold">НЕЗАВИСИМАЯ ОЦЕНКА КАЧЕСТВА</h1>
                  <small className="text-muted">Административная панель</small>
                </div>
              </div>
            </div>

            {/* ЦЕНТРАЛЬНАЯ ЧАСТЬ - НАВИГАЦИЯ ДЛЯ ДЕСКТОПА */}
            <nav className="d-none d-lg-flex align-items-center mx-4 flex-grow-1 justify-content-center">
              <div className="d-flex align-items-center gap-1">
                <button
                  onClick={() => handleNavigation('/profile')}
                  className={`nav-header-btn ${getActiveTab() === 'profile' ? 'active' : ''}`}
                >
                  <i className="bi bi-person me-2"></i>
                  Профиль
                </button>
                <button
                  onClick={() => handleNavigation('/surveys')}
                  className={`nav-mobile-sidebar-btn ${getActiveTab() === 'surveys' ? 'active' : ''}`}
                >
                  <i className="bi bi-clipboard-data"></i>
                  <span>Анкеты</span>
                </button>


                <button
                  onClick={() => handleNavigation('/users')}
                  className={`nav-header-btn ${getActiveTab() === 'users' ? 'active' : ''}`}
                >
                  <i className="bi bi-people me-2"></i>
                  Пользователи
                </button>
                
                <button
                  onClick={() => handleNavigation('/organizations')}
                  className={`nav-header-btn ${getActiveTab() === 'organizations' ? 'active' : ''}`}
                >
                  <i className="bi bi-building me-2"></i>
                  Организации
                </button>

                <button
                  onClick={() => handleNavigation('/notifications')}
                  className={`nav-header-btn ${getActiveTab() === 'notifications' ? 'active' : ''}`}
                >
                  <i className="bi bi-bell me-2"></i>
                  Уведомления
                  {unreadCount > 0 && (
                    <Badge bg="danger" className="ms-2 notification-badge">
                      {unreadCount > 99 ? '99+' : unreadCount}
                    </Badge>
                  )}
                </button>
              </div>
            </nav>

            {/* ПРАВАЯ ЧАСТЬ */}
            <div className="d-flex align-items-center gap-3">
              
              {/* УВЕДОМЛЕНИЯ ДЛЯ ДЕСКТОПА */}
              <div className="d-none d-md-block notification-dropdown-desktop">
                <NotificationBell isMobile={false} />
              </div>
              
              {/* УВЕДОМЛЕНИЯ ДЛЯ МОБИЛЬНЫХ */}
              <div className="d-md-none notification-dropdown-mobile">
                <NotificationBell isMobile={true} />
              </div>
              
              {/* АВАТАР ПОЛЬЗОВАТЕЛЯ */}
              <div className="position-relative" ref={userMenuRef}>
                <button
                  onClick={toggleUserMenu}
                  className="user-avatar-btn d-flex align-items-center gap-2 border-0 bg-transparent p-2 rounded-3"
                >
                  <div className="position-relative">
                    {user.telegram_photo_url ? (
                      <img 
                        src={user.telegram_photo_url} 
                        alt="Avatar"
                        className="user-avatar-img"
                      />
                    ) : (
                      <div className="user-avatar-placeholder">
                        {getInitials()}
                      </div>
                    )}
                    
                    <div 
                      className={`user-role-badge ${user.role}`}
                      title={user.role === 'admin' ? 'Администратор' : 
                             user.role === 'moderator' ? 'Модератор' : 'Пользователь'}
                    >
                      <i className={getRoleIcon(user.role)}></i>
                    </div>
                  </div>
                  
                  <div className="d-none d-xl-block text-start">
                    <div className="fw-semibold" style={{ fontSize: '0.9rem', lineHeight: '1.2' }}>
                      {getFullName()}
                    </div>
                    <small className="text-muted" style={{ fontSize: '0.75rem' }}>
                      {user.role === 'admin' ? 'Администратор' : 
                       user.role === 'moderator' ? 'Модератор' : 'Пользователь'}
                    </small>
                  </div>
                  
                  <i 
                    className={`bi bi-chevron-down text-muted d-none d-md-block ${
                      showUserMenu ? 'rotate-180' : ''
                    }`}
                    style={{ transition: 'transform 0.3s ease' }}
                  ></i>
                </button>

                {/* Выпадающее меню пользователя */}
                {showUserMenu && (
                  <div 
                    className="position-absolute bg-white rounded-3 shadow-lg border mt-2 user-dropdown-menu"
                    style={{
                      top: '100%',
                      right: '0',
                      minWidth: '260px',
                      zIndex: 1050,
                      animation: 'fadeInDown 0.2s ease-out'
                    }}
                  >
                    {/* Заголовок */}
                    <div className="p-4 border-bottom bg-light rounded-top-3">
                      <div className="fw-bold text-dark mb-1">{getFullName()}</div>
                      <div className="text-muted small mb-2">
                        {user.role === 'admin' ? 'Администратор' : 
                         user.role === 'moderator' ? 'Модератор' : 'Пользователь'}
                      </div>
                      <div className="text-primary small">
                        <i className="bi bi-envelope me-1"></i>
                        {user.email || 'Email не указан'}
                      </div>
                    </div>
                    
                    {/* Пункты меню */}
                    <div className="p-3">
                      <button
                        className="dropdown-item w-100 text-start border-0 bg-transparent p-3 rounded d-flex align-items-center gap-3"
                        onClick={() => {
                          setShowUserMenu(false);
                          handleNavigation('/profile');
                        }}
                      >
                        <i className="bi bi-person-fill text-primary fs-5" style={{ width: '20px' }}></i>
                        <div>
                          <div className="fw-medium">Мой профиль</div>
                          <small className="text-muted">Управление профилем</small>
                        </div>
                      </button>
             

                      <button
                        className="dropdown-item w-100 text-start border-0 bg-transparent p-3 rounded d-flex align-items-center gap-3"
                        onClick={() => {
                          setShowUserMenu(false);
                          console.log('Settings clicked');
                        }}
                      >
                        <i className="bi bi-info-circle-fill text-primary fs-5" style={{ width: '20px' }}></i>
                        <div>
                          <div className="fw-medium">Справка</div>
                          <small className="text-muted">Информация о системе</small>
                        </div>
                      </button>

                      <hr className="my-2" />
                      
                      <button
                        className="dropdown-item w-100 text-start border-0 bg-transparent p-3 rounded d-flex align-items-center gap-3 text-danger"
                        onClick={handleLogout}
                      >
                        <i className="bi bi-box-arrow-right fs-5" style={{ width: '20px' }}></i>
                        <div>
                          <div className="fw-medium">Выход</div>
                          <small className="text-muted">Завершить сеанс</small>
                        </div>
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </Container>
      </header>

      {/* МОБИЛЬНОЕ МЕНЮ */}
      <Offcanvas 
        show={showMobileMenu} 
        onHide={() => setShowMobileMenu(false)}
        placement="start"
        className="mobile-sidebar"
        ref={mobileMenuRef}
      >
        <Offcanvas.Header closeButton className="mobile-sidebar-header">
          <Offcanvas.Title>
            <div className="d-flex align-items-center">
              <div className="text-primary fs-3">
                <i className="bi bi-clipboard-check"></i>
              </div>
              <div>
                <h6 className="mb-0 text-primary fw-bold">НЕЗАВИСИМАЯ ОЦЕНКА КАЧЕСТВА</h6>
                <small className="text-muted">Админпанель</small>
              </div>
            </div>
          </Offcanvas.Title>
        </Offcanvas.Header>
        <Offcanvas.Body className="mobile-sidebar-body">
          <div className="d-flex flex-column gap-1 mb-4">
            <button
              onClick={() => handleNavigation('/profile')}
              className={`nav-mobile-sidebar-btn ${getActiveTab() === 'profile' ? 'active' : ''}`}
            >
              <i className="bi bi-person"></i>
              <span>Профиль</span>
            </button>
            
            <button
                  onClick={() => handleNavigation('/surveys')}
                  className={`nav-mobile-sidebar-btn ${getActiveTab() === 'surveys' ? 'active' : ''}`}
                >
                  <i className="bi bi-clipboard-data"></i>
                  <span>Анкеты</span>
              </button>

            <button
              onClick={() => handleNavigation('/users')}
              className={`nav-mobile-sidebar-btn ${getActiveTab() === 'users' ? 'active' : ''}`}
            >
              <i className="bi bi-people"></i>
              <span>Пользователи</span>
            </button>
            
            <button
              onClick={() => handleNavigation('/organizations')}
              className={`nav-mobile-sidebar-btn ${getActiveTab() === 'organizations' ? 'active' : ''}`}
            >
              <i className="bi bi-building"></i>
              <span>Организации</span>
            </button>

            <button
              onClick={() => handleNavigation('/notifications')}
              className={`nav-mobile-sidebar-btn ${getActiveTab() === 'notifications' ? 'active' : ''}`}
            >
              <i className="bi bi-bell"></i>
              <span>Уведомления</span>
              {unreadCount > 0 && (
                <Badge bg="danger" className="notification-badge">
                  {unreadCount > 99 ? '99+' : unreadCount}
                </Badge>
              )}
            </button>
          </div>
          
          <div className="mt-auto pt-4 border-top">
            <div className="d-flex align-items-center gap-3 mb-4 p-3 bg-light rounded-3">
              {user.telegram_photo_url ? (
                <img 
                  src={user.telegram_photo_url} 
                  alt="Avatar"
                  className="user-avatar-img"
                  style={{ width: '48px', height: '48px' }}
                />
              ) : (
                <div 
                  className="user-avatar-placeholder"
                  style={{ width: '48px', height: '48px' }}
                >
                  {getInitials()}
                </div>
              )}
              <div className="flex-grow-1">
                <div className="fw-semibold">{getFullName()}</div>
                <small className="text-muted">
                  {user.role === 'admin' ? 'Администратор' : 
                   user.role === 'moderator' ? 'Модератор' : 'Пользователь'}
                </small>
              </div>
            </div>
            <button
              className="btn btn-outline-danger w-100 btn-sm py-2"
              onClick={handleLogout}
            >
              <i className="bi bi-box-arrow-right me-2"></i>
              Выход из системы
            </button>
          </div>
        </Offcanvas.Body>
      </Offcanvas>

      {/* Content */}
      <main className="dashboard-content flex-grow-1">
        <Container fluid className="py-4 px-3 px-lg-4">
          <Outlet />
        </Container>
      </main>

      {/* Footer */}
      <footer className="bg-white border-top py-3 mt-auto">
        <Container fluid className="px-3 px-lg-4">
          <div className="text-center">
            <p className="text-muted small mb-0">
              <a href="https://hakster.ru/" target="_blank">HAKSTER</a> | {currentYear}  <small className="text-muted">v{APP_VERSION}</small> 
            </p>
          </div>
        </Container>
      </footer>
    </div>
  );
};

export default Dashboard;